import {useCallback, useEffect, useMemo, useState} from 'react'
import {useApolloClient, useLazyQuery} from '@apollo/client'
import {
  Alert,
  Badge,
  Button,
  Form,
  Modal,
  Pagination,
  ProgressBar,
  Spinner,
  Table,
} from 'react-bootstrap'
import {defineMessages, useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import {useUploadFile} from '../../../hooks/useUploadFile'
import {useAsyncTaskPolling} from '../../../hooks/useAsyncTaskPolling'
import {
  UPDATE_POLICIES_BY_CARRIER_STR,
  UPDATE_POLICIES_BY_CARRIER_ASYNC_STR,
} from '../../../gql/mutations/policyMutation'
import {
  GET_ALL_CARRIERS_LIST,
  GET_ALL_CARRIERS_WITH_STATUS_LIST,
} from '../../../gql/queries/carriersQuery'
import {
  GET_CARRIER_IMPORT_ASYNC_TASK,
  GET_CARRIER_IMPORT_BATCH_UNMATCHED,
} from '../../../gql/queries/carrierImportQueries'
import {DateInput} from 'app/tfi/components/DateInput'
import {MultiSelectWithActions} from 'app/tfi/components/MultiSelectWithActions'
import {BadgeStatePolicy} from '../../onePolicy/components/BadgeStatePolicy'
import {exportUnmatchedCsv, CarrierImportUnmatchedRow} from './carrierImportExport'
import {parseId} from 'app/tfi/helpers'

// ─────────────────────────────────────────────────────────────────────────────
// Feature flag: set to false to revert to the legacy synchronous upload.
// When false, the app calls the original sync mutation and this file is the
// only thing that needs to change — no backend code is affected.
const USE_ASYNC_CARRIER_IMPORT = true
// ─────────────────────────────────────────────────────────────────────────────

interface CarrierImportSyncResult {
  updatePoliciesByCarrier: {
    success: boolean
    message: string
    processedCount: number
    matchedCount: number
    unmatchedCount: number
    errorsCount: number
    unmatchedRows: CarrierImportUnmatchedRow[]
  }
}

interface CarrierImportAsyncInitResult {
  updatePoliciesByCarrierAsync: {
    success: boolean
    message: string
    task?: {
      taskId: string
      status: string
      progressPercentage?: number | null
      progressMessage?: string | null
    } | null
  }
}

interface ImportResult {
  success: boolean
  message: string
  processedCount: number
  matchedCount: number
  unmatchedCount: number
  errorsCount: number
  unmatchedRows: CarrierImportUnmatchedRow[]
}

interface CarrierItem {
  id: string
  name: string
  enabled?: boolean
  aliasIds?: string[]
}

interface PolicyStatusItem {
  code: string
  title: string
  color?: string
}

interface Props {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  carriers: CarrierItem[]
  policyStatuses: PolicyStatusItem[]
  policyTypes: any[]
}

const M = defineMessages({
  title: {id: 'policies.list.byCarrier.title', defaultMessage: 'Update Policies by Carrier'},
  fieldCarrier: {
    id: 'policies.list.byCarrier.field.carrier',
    defaultMessage: 'File carrier / format',
  },
  fieldTargetCarriers: {
    id: 'policies.list.byCarrier.field.targetCarriers',
    defaultMessage: 'Target carriers',
  },
  targetCarriersHint: {
    id: 'policies.list.byCarrier.hint.targetCarriers',
    defaultMessage: 'Only policies assigned to these carriers can be matched or updated',
  },
  fieldPolicyTypes: {
    id: 'policies.list.byCarrier.field.policyTypes',
    defaultMessage: 'Policy types',
  },
  policyTypesHint: {
    id: 'policies.list.byCarrier.hint.policyTypes',
    defaultMessage: 'Only policies with these types can be matched or updated',
  },
  policyDateFrom: {
    id: 'policies.list.byCarrier.field.policyDateFrom',
    defaultMessage: 'Policy date from',
  },
  policyDateTo: {
    id: 'policies.list.byCarrier.field.policyDateTo',
    defaultMessage: 'Policy date to',
  },
  dateCriteria: {id: 'policies.list.byCarrier.field.dateCriteria', defaultMessage: 'Date criteria'},
  dateCriteriaEffective: {
    id: 'policies.list.byCarrier.dateCriteria.effective',
    defaultMessage: 'Policies Effective',
  },
  dateCriteriaSubmitted: {
    id: 'policies.list.byCarrier.dateCriteria.submitted',
    defaultMessage: 'Policies Submitted',
  },
  policyDateRangeHint: {
    id: 'policies.list.byCarrier.hint.policyDateRange',
    defaultMessage: 'Only policies within this inclusive date range can be matched or updated',
  },
  loadingCarriers: {
    id: 'policies.list.byCarrier.loadingCarriers',
    defaultMessage: 'Loading carriers...',
  },
  selectCarrier: {id: 'policies.list.byCarrier.selectCarrier', defaultMessage: 'Select carrier...'},
  supportedCarriers: {
    id: 'policies.list.byCarrier.supportedCarriers',
    defaultMessage:
      'Supported carriers: Ambetter, Oscar, BCBS, Blue Shield of California, Molina, Anthem, Imperial, Sentara, Highmark, Medica, Select Health, United Healthcare, United Health One - Supplemental',
  },
  asOfDate: {id: 'policies.list.byCarrier.asOfDate', defaultMessage: 'As-of Date'},
  asOfDateHint: {
    id: 'policies.list.byCarrier.asOfDateHint',
    defaultMessage: 'The date through which the data in this carrier file is current',
  },
  fileCsv: {id: 'policies.list.byCarrier.fileCsv', defaultMessage: 'File (CSV)'},
  loadingUnmatched: {
    id: 'policies.list.byCarrier.loadingUnmatched',
    defaultMessage: 'Loading unmatched rows...',
  },
  uploadFailed: {id: 'policies.list.byCarrier.uploadFailed', defaultMessage: 'Upload failed'},
  statProcessed: {id: 'policies.list.byCarrier.stat.processed', defaultMessage: 'Processed:'},
  statMatched: {id: 'policies.list.byCarrier.stat.matched', defaultMessage: 'Matched:'},
  statUnmatched: {id: 'policies.list.byCarrier.stat.unmatched', defaultMessage: 'Unmatched:'},
  statErrors: {id: 'policies.list.byCarrier.stat.errors', defaultMessage: 'Errors:'},
  unmatchedRows: {
    id: 'policies.list.byCarrier.unmatchedRows',
    defaultMessage: 'Unmatched Rows ({count})',
  },
  ofTotal: {id: 'policies.list.byCarrier.ofTotal', defaultMessage: '{shown} of {total}'},
  exportCsv: {id: 'policies.list.byCarrier.exportCsv', defaultMessage: 'Export Unmatched CSV'},
  phSearch: {
    id: 'policies.list.byCarrier.phSearch',
    defaultMessage: 'Search by Member ID or Name...',
  },
  thRow: {id: 'policies.list.byCarrier.th.row', defaultMessage: 'Row'},
  thMemberId: {id: 'policies.list.byCarrier.th.memberId', defaultMessage: 'Member ID'},
  thName: {id: 'policies.list.byCarrier.th.name', defaultMessage: 'Name'},
  thCarrierStatus: {
    id: 'policies.list.byCarrier.th.carrierStatus',
    defaultMessage: 'Carrier Status',
  },
  thSystemStatus: {id: 'policies.list.byCarrier.th.systemStatus', defaultMessage: 'System Status'},
  thAgent: {id: 'policies.list.byCarrier.th.agent', defaultMessage: 'Agent'},
  thReason: {id: 'policies.list.byCarrier.th.reason', defaultMessage: 'Reason'},
  pageOf: {id: 'policies.list.byCarrier.pageOf', defaultMessage: 'Page {page} of {total}'},
  btnUploading: {id: 'policies.list.byCarrier.btn.uploading', defaultMessage: 'Uploading...'},
  processing: {id: 'policies.list.byCarrier.processing', defaultMessage: 'Processing...'},
  btnWorking: {id: 'policies.list.byCarrier.btn.working', defaultMessage: 'Working...'},
  btnUploadProcess: {
    id: 'policies.list.byCarrier.btn.uploadProcess',
    defaultMessage: 'Upload & Process',
  },
  toastFillFields: {
    id: 'policies.list.byCarrier.toast.fillFields',
    defaultMessage: 'Please fill all required fields',
  },
  toastInvalidDateRange: {
    id: 'policies.list.byCarrier.toast.invalidDateRange',
    defaultMessage: 'The policy start date cannot be after the end date',
  },
  toastFailedStart: {
    id: 'policies.list.byCarrier.toast.failedStart',
    defaultMessage: 'Failed to start import task',
  },
  toastImportCompleted: {
    id: 'policies.list.byCarrier.toast.importCompleted',
    defaultMessage: 'Import completed successfully',
  },
  msgImportCompleted: {
    id: 'policies.list.byCarrier.msg.importCompleted',
    defaultMessage: 'Import completed',
  },
  importFailed: {
    id: 'policies.list.byCarrier.importFailed',
    defaultMessage: 'Import failed: {message}',
  },
  progTaskQueued: {id: 'policies.list.byCarrier.prog.taskQueued', defaultMessage: 'Task queued...'},
  close: {id: 'common.action.close', defaultMessage: 'Close'},
  cancel: {id: 'common.action.cancel', defaultMessage: 'Cancel'},
})

const SUPPORTED_CARRIERS = [
  'ambetter',
  'oscar',
  'blue cross',
  'blue shield',
  'bcbs',
  'molina',
  'anthem',
  'imperial',
  'sentara',
  'jefferson',
  'independence',
  'amerihealth',
  'ameri health',
  'cigna',
  'wellpoint',
  'alliant',
  'highmark',
  'medica',
  'select health',
  'selecthealth',
]
const ANTHEM_CARRIER_FALLBACK: CarrierItem = {id: '17', name: 'Anthem', enabled: false}
const PAGE_SIZE = 25

const getDefaultPolicyDateRange = () => {
  const today = new Date()
  const year = today.getFullYear()
  const month = String(today.getMonth() + 1).padStart(2, '0')
  const day = String(today.getDate()).padStart(2, '0')
  return {from: `${year}-01-01`, to: `${year}-${month}-${day}`}
}

export const ModalUpdatePoliciesByCarrier: React.FC<Props> = ({
  isOpen,
  onClose,
  onSuccess,
  carriers,
  policyStatuses,
  policyTypes,
}) => {
  const intl = useIntl()
  const dash = '-' // non-translatable empty-cell placeholder (hoisted)
  const percentSign = '%' // non-translatable symbol (hoisted)
  const [carrierId, setCarrierId] = useState('')
  const [targetCarrierIds, setTargetCarrierIds] = useState<string[]>([])
  const [policyTypeIds, setPolicyTypeIds] = useState<string[]>([])
  const [policyDateFrom, setPolicyDateFrom] = useState<string>(
    () => getDefaultPolicyDateRange().from
  )
  const [policyDateTo, setPolicyDateTo] = useState<string>(() => getDefaultPolicyDateRange().to)
  const [dateCriteria, setDateCriteria] = useState<'submitted' | 'effective'>('submitted')
  const [asOfDate, setAsOfDate] = useState<string>('')
  const [file, setFile] = useState<File | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [fetchedCarriers, setFetchedCarriers] = useState<CarrierItem[]>([])
  const [catalogCarriers, setCatalogCarriers] = useState<CarrierItem[]>([])
  const [loadingCarriers, setLoadingCarriers] = useState(false)

  // Unified result state — populated by either sync or async path
  const [importResult, setImportResult] = useState<ImportResult | null>(null)

  // Async-specific state
  const [asyncTaskId, setAsyncTaskId] = useState<string | null>(null)
  const [asyncProgress, setAsyncProgress] = useState<{pct: number; msg: string} | null>(null)
  const [fetchingUnmatched, setFetchingUnmatched] = useState(false)

  const [uploadFn, {loading: isSyncUploading, error: syncError}] =
    useUploadFile<CarrierImportSyncResult>()
  const [uploadAsyncFn, {loading: isAsyncUploading}] = useUploadFile<CarrierImportAsyncInitResult>()

  const [fetchUnmatched] = useLazyQuery(GET_CARRIER_IMPORT_BATCH_UNMATCHED, {
    fetchPolicy: 'network-only',
  })

  const client = useApolloClient()

  // Polling hook — only active when asyncTaskId is set
  const {task: pollingTask, isPolling} = useAsyncTaskPolling({
    taskId: asyncTaskId,
    query: GET_CARRIER_IMPORT_ASYNC_TASK,
    dataKey: 'carrierImportAsyncTask',
    pollingInterval: 2000,
    onSuccess: async (resultStr) => {
      setAsyncTaskId(null)
      setAsyncProgress(null)
      try {
        const parsed = JSON.parse(resultStr)
        // Fetch unmatched rows from the DB using the batch_id from the task result
        let unmatchedRows: CarrierImportUnmatchedRow[] = []
        if (parsed.batch_id) {
          setFetchingUnmatched(true)
          try {
            const res = await fetchUnmatched({variables: {batchId: parsed.batch_id}})
            unmatchedRows = res.data?.carrierImportBatchUnmatched ?? []
          } catch {
            // Non-critical: show counts even if unmatched detail fails
          } finally {
            setFetchingUnmatched(false)
          }
        }
        setImportResult({
          success: parsed.success ?? true,
          message: parsed.message ?? intl.formatMessage(M.msgImportCompleted),
          processedCount: parsed.processed_count ?? 0,
          matchedCount: parsed.matched_count ?? 0,
          unmatchedCount: parsed.unmatched_count ?? 0,
          errorsCount: parsed.errors_count ?? 0,
          unmatchedRows,
        })
      } catch {
        toast.success(intl.formatMessage(M.toastImportCompleted))
      }
    },
    onError: (errMsg) => {
      setAsyncTaskId(null)
      setAsyncProgress(null)
      toast.error(intl.formatMessage(M.importFailed, {message: errMsg}))
    },
  })

  // Keep progress bar in sync with polling task
  useEffect(() => {
    if (pollingTask) {
      setAsyncProgress({
        pct: pollingTask.progressPercentage ?? 0,
        msg: pollingTask.progressMessage ?? intl.formatMessage(M.processing),
      })
    }
  }, [pollingTask])

  useEffect(() => {
    if (!isOpen) return

    setLoadingCarriers(true)
    const searchTerms = [
      'ambetter',
      'oscar',
      'blue cross',
      'blue shield',
      'bcbs',
      'molina',
      'anthem',
      'imperial',
      'sentara',
      'jefferson',
      'independence',
      'amerihealth',
      'cigna',
      'wellpoint',
      'alliant',
      'highmark',
      'medica',
      'select health',
      'selecthealth',
      'united',
    ]

    const supportedCarrierRequests = searchTerms.map((name) =>
      client.query({
        query: GET_ALL_CARRIERS_WITH_STATUS_LIST,
        variables: {status: 'all', name, first: 20},
        fetchPolicy: 'network-only',
      })
    )

    Promise.all([
      Promise.all(supportedCarrierRequests),
      client.query({query: GET_ALL_CARRIERS_LIST, fetchPolicy: 'network-only'}),
    ])
      .then(([results, catalogResult]) => {
        const seen = new Set<string>()
        const merged: CarrierItem[] = []
        for (const result of results) {
          const edges = result.data?.allInsuranceOperators?.edges || []
          for (const edge of edges) {
            const node = edge?.node
            if (node && !seen.has(String(node.id))) {
              seen.add(String(node.id))
              merged.push(node)
            }
          }
        }
        setFetchedCarriers(merged)
        setCatalogCarriers(catalogResult.data?.allInsuranceOperatorsList || [])
      })
      .catch(() => {})
      .finally(() => setLoadingCarriers(false))
  }, [isOpen, client])

  const allCarriers: CarrierItem[] = useMemo(() => {
    const seenIds = new Set<string>()
    const byName = new Map<string, CarrierItem>()
    const base: CarrierItem[] = []
    for (const carrier of [...(carriers || []), ...catalogCarriers, ...fetchedCarriers]) {
      const id = String(parseId(carrier?.id || ''))
      if (!id || id === '0' || seenIds.has(id)) continue
      seenIds.add(id)

      const nameKey = (carrier?.name || '').trim().toLowerCase().replace(/\s+/g, ' ') || id
      const existing = byName.get(nameKey)
      if (existing) {
        existing.aliasIds = [...(existing.aliasIds || [existing.id]), id]
        continue
      }

      const normalizedCarrier = {...carrier, id, aliasIds: [id]}
      byName.set(nameKey, normalizedCarrier)
      base.push(normalizedCarrier)
    }
    const hasAnthem = base.some((carrier) => {
      const name = carrier?.name?.trim().toLowerCase() || ''
      return String(carrier?.id) === ANTHEM_CARRIER_FALLBACK.id || name === 'anthem'
    })
    if (hasAnthem) return base
    return [...base, ANTHEM_CARRIER_FALLBACK]
  }, [carriers, catalogCarriers, fetchedCarriers])

  const supportedCarriers = useMemo(
    () =>
      allCarriers
        .filter((carrier) => {
          const name = carrier?.name?.toLowerCase() || ''
          if (name.includes('capital blue cross')) return false
          if (name.includes('cigna') && name !== 'cigna') return false
          if (
            (name.includes('amerihealth') || name.includes('ameri health')) &&
            name.includes('caritas')
          )
            return false
          if (name === 'united healthcare' || name === 'united health one - supplemental')
            return true
          if (name === 'cigna') return true
          if (name === 'medica') return true
          return SUPPORTED_CARRIERS.some((sc) => name.includes(sc))
        })
        .sort((a, b) => (a?.name || '').localeCompare(b?.name || '')),
    [allCarriers]
  )

  const targetCarrierOptions = useMemo(
    () =>
      allCarriers
        .map((carrier) => ({
          value: String(carrier.id),
          label: carrier.name,
          carrierIds: carrier.aliasIds || [String(carrier.id)],
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [allCarriers]
  )

  const policyTypeOptions = useMemo(
    () =>
      (policyTypes || []).map((category) => ({
        label: category.name,
        options: (category.policyTypeSet?.edges || []).map(({node}: any) => ({
          value: String(parseId(node.id)),
          label: node.typePolicy,
        })),
      })),
    [policyTypes]
  )

  const selectedTargetCarriers = useMemo(
    () =>
      targetCarrierOptions.filter((option) =>
        option.carrierIds.some((id) => targetCarrierIds.includes(id))
      ),
    [targetCarrierIds, targetCarrierOptions]
  )

  const selectedPolicyTypes = useMemo(() => {
    const options = policyTypeOptions.flatMap((category) => category.options)
    return options.filter((option) => policyTypeIds.includes(option.value))
  }, [policyTypeIds, policyTypeOptions])

  const statusTitleByCode = useMemo(() => {
    const map: Record<string, string> = {}
    for (const status of policyStatuses || []) {
      if (status?.code) map[status.code] = status.title || ''
    }
    return map
  }, [policyStatuses])

  const statusColorByCode = useMemo(() => {
    const map: Record<string, string> = {}
    for (const status of policyStatuses || []) {
      if (status?.code) map[status.code] = status.color || 'secondary'
    }
    return map
  }, [policyStatuses])

  const buildFormData = useCallback(
    (mutationStr: string) => {
      if (
        !carrierId ||
        targetCarrierIds.length === 0 ||
        policyTypeIds.length === 0 ||
        !policyDateFrom ||
        !policyDateTo ||
        !asOfDate ||
        !file
      )
        return null
      const formData = new FormData()
      formData.append('0', file, file.name)
      formData.append(
        'operations',
        JSON.stringify({
          query: mutationStr,
          variables: {
            carrierId: parseId(carrierId),
            targetCarrierIds: targetCarrierIds.map((id) => parseId(id)),
            policyTypeIds: policyTypeIds.map((id) => parseId(id)),
            policyDateFrom,
            policyDateTo,
            dateCriteria,
            asOfDate,
            file: null,
          },
        })
      )
      formData.append('map', JSON.stringify({'0': ['variables.file']}))
      return formData
    },
    [
      carrierId,
      targetCarrierIds,
      policyTypeIds,
      policyDateFrom,
      policyDateTo,
      dateCriteria,
      asOfDate,
      file,
    ]
  )

  const handleUpload = useCallback(async () => {
    if (
      !carrierId ||
      targetCarrierIds.length === 0 ||
      policyTypeIds.length === 0 ||
      !policyDateFrom ||
      !policyDateTo ||
      !asOfDate ||
      !file
    ) {
      toast.error(intl.formatMessage(M.toastFillFields))
      return
    }
    if (policyDateFrom > policyDateTo) {
      toast.error(intl.formatMessage(M.toastInvalidDateRange))
      return
    }

    if (USE_ASYNC_CARRIER_IMPORT) {
      // ── Async path ──────────────────────────────────────────────────────────
      const formData = buildFormData(UPDATE_POLICIES_BY_CARRIER_ASYNC_STR)
      if (!formData) return
      try {
        const result = await uploadAsyncFn(formData)
        const payload = result?.updatePoliciesByCarrierAsync
        if (!payload?.success || !payload?.task?.taskId) {
          toast.error(payload?.message || intl.formatMessage(M.toastFailedStart))
          return
        }
        setAsyncProgress({pct: 0, msg: intl.formatMessage(M.progTaskQueued)})
        setAsyncTaskId(payload.task.taskId)
      } catch {
        // error already set by useUploadFile
      }
    } else {
      // ── Sync path (legacy — untouched) ──────────────────────────────────────
      const formData = buildFormData(UPDATE_POLICIES_BY_CARRIER_STR)
      if (!formData) return
      try {
        const result = await uploadFn(formData)
        const payload = result?.updatePoliciesByCarrier
        if (payload) {
          setImportResult({
            success: payload.success,
            message: payload.message,
            processedCount: payload.processedCount,
            matchedCount: payload.matchedCount,
            unmatchedCount: payload.unmatchedCount,
            errorsCount: payload.errorsCount,
            unmatchedRows: payload.unmatchedRows ?? [],
          })
        }
      } catch {
        // error handled by useUploadFile
      }
    }
  }, [
    carrierId,
    targetCarrierIds,
    policyTypeIds,
    policyDateFrom,
    policyDateTo,
    asOfDate,
    file,
    buildFormData,
    uploadFn,
    uploadAsyncFn,
  ])

  const handleClose = () => {
    setCarrierId('')
    setTargetCarrierIds([])
    setPolicyTypeIds([])
    const defaultDateRange = getDefaultPolicyDateRange()
    setPolicyDateFrom(defaultDateRange.from)
    setPolicyDateTo(defaultDateRange.to)
    setDateCriteria('submitted')
    setAsOfDate('')
    setFile(null)
    setSearchQuery('')
    setCurrentPage(1)
    setAsyncTaskId(null)
    setAsyncProgress(null)
    if (importResult?.success) {
      onSuccess()
    }
    setImportResult(null)
    onClose()
  }

  // Filter & paginate unmatched rows
  const filteredRows = useMemo(() => {
    const rows = importResult?.unmatchedRows ?? []
    if (!searchQuery.trim()) return rows
    const q = searchQuery.trim().toLowerCase()
    return rows.filter(
      (row) =>
        (row.memberId && row.memberId.toLowerCase().includes(q)) ||
        (row.name && row.name.toLowerCase().includes(q))
    )
  }, [importResult?.unmatchedRows, searchQuery])

  const totalFiltered = filteredRows.length
  const totalPages = Math.max(1, Math.ceil(totalFiltered / PAGE_SIZE))
  const safePage = Math.min(currentPage, totalPages)
  const paginatedRows = filteredRows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)

  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
    setCurrentPage(1)
  }

  const paginationItems = useMemo(() => {
    const items: JSX.Element[] = []
    const maxVisible = 5
    let start = Math.max(1, safePage - Math.floor(maxVisible / 2))
    const end = Math.min(totalPages, start + maxVisible - 1)
    if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1)
    for (let p = start; p <= end; p++) {
      items.push(
        <Pagination.Item key={p} active={p === safePage} onClick={() => setCurrentPage(p)}>
          {p}
        </Pagination.Item>
      )
    }
    return items
  }, [safePage, totalPages])

  const isWorking = isSyncUploading || isAsyncUploading || isPolling || fetchingUnmatched

  return (
    <Modal show={isOpen} onHide={handleClose} size='xl' backdrop='static'>
      <Modal.Header closeButton>
        <Modal.Title>{intl.formatMessage(M.title)}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!importResult ? (
          <>
            <Form.Group className='mb-3'>
              <Form.Label>{intl.formatMessage(M.fieldCarrier)}</Form.Label>
              <Form.Select
                value={carrierId}
                onChange={(e) => {
                  const value = e.target.value
                  setCarrierId(value)
                  const targetOption = targetCarrierOptions.find((option) => option.value === value)
                  setTargetCarrierIds(value ? targetOption?.carrierIds || [value] : [])
                }}
                disabled={isWorking || loadingCarriers}
              >
                <option value=''>
                  {loadingCarriers
                    ? intl.formatMessage(M.loadingCarriers)
                    : intl.formatMessage(M.selectCarrier)}
                </option>
                {supportedCarriers.map((carrier) => (
                  <option key={carrier.id} value={carrier.id}>
                    {carrier.name}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className='text-muted'>
                {intl.formatMessage(M.supportedCarriers)}
              </Form.Text>
            </Form.Group>

            <Form.Group className='mb-3'>
              <Form.Label>{intl.formatMessage(M.fieldTargetCarriers)}</Form.Label>
              <MultiSelectWithActions
                value={selectedTargetCarriers}
                options={targetCarrierOptions}
                onChange={(selected: Array<{value: string; carrierIds: string[]}>) =>
                  setTargetCarrierIds(
                    Array.from(
                      new Set(
                        (selected || []).flatMap(
                          (option) => option.carrierIds || [String(option.value)]
                        )
                      )
                    )
                  )
                }
                isDisabled={isWorking}
              />
              <Form.Text className='text-muted'>
                {intl.formatMessage(M.targetCarriersHint)}
              </Form.Text>
            </Form.Group>

            <Form.Group className='mb-3'>
              <Form.Label>{intl.formatMessage(M.fieldPolicyTypes)}</Form.Label>
              <MultiSelectWithActions
                value={selectedPolicyTypes}
                options={policyTypeOptions}
                onChange={(selected: Array<{value: string}>) =>
                  setPolicyTypeIds((selected || []).map((option) => String(option.value)))
                }
                isDisabled={isWorking}
              />
              <Form.Text className='text-muted'>{intl.formatMessage(M.policyTypesHint)}</Form.Text>
            </Form.Group>

            <div className='row g-3 mb-1'>
              <div className='col-md-4'>
                <DateInput
                  id='policyDateFrom'
                  name='policyDateFrom'
                  label={intl.formatMessage(M.policyDateFrom)}
                  value={policyDateFrom || null}
                  disabled={isWorking}
                  onChange={(_field, value) => setPolicyDateFrom(value ?? '')}
                />
              </div>
              <div className='col-md-4'>
                <DateInput
                  id='policyDateTo'
                  name='policyDateTo'
                  label={intl.formatMessage(M.policyDateTo)}
                  value={policyDateTo || null}
                  disabled={isWorking}
                  onChange={(_field, value) => setPolicyDateTo(value ?? '')}
                />
              </div>
              <div className='col-md-4'>
                <Form.Group>
                  <Form.Label>{intl.formatMessage(M.dateCriteria)}</Form.Label>
                  <Form.Select
                    value={dateCriteria}
                    disabled={isWorking}
                    onChange={(event) =>
                      setDateCriteria(event.target.value as 'submitted' | 'effective')
                    }
                  >
                    <option value='effective'>{intl.formatMessage(M.dateCriteriaEffective)}</option>
                    <option value='submitted'>{intl.formatMessage(M.dateCriteriaSubmitted)}</option>
                  </Form.Select>
                </Form.Group>
              </div>
            </div>
            <Form.Text className='text-muted d-block mb-3'>
              {intl.formatMessage(M.policyDateRangeHint)}
            </Form.Text>

            <Form.Group className='mb-3'>
              <DateInput
                id='asOfDate'
                name='asOfDate'
                label={intl.formatMessage(M.asOfDate)}
                value={asOfDate || null}
                disabled={isWorking}
                onChange={(_field, value) => setAsOfDate(value ?? '')}
              />
              <Form.Text className='text-muted'>{intl.formatMessage(M.asOfDateHint)}</Form.Text>
            </Form.Group>

            <Form.Group className='mb-3'>
              <Form.Label>{intl.formatMessage(M.fileCsv)}</Form.Label>
              <Form.Control
                type='file'
                accept='.csv,.xlsx,.xls'
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setFile(e.target.files?.[0] || null)
                }}
                disabled={isWorking}
              />
            </Form.Group>

            {/* Progress bar — only visible during async processing */}
            {asyncProgress !== null && (
              <div className='mb-3'>
                <div className='d-flex justify-content-between mb-1'>
                  <small className='text-muted'>{asyncProgress.msg}</small>
                  <small className='text-muted fw-bold'>
                    {asyncProgress.pct}
                    {percentSign}
                  </small>
                </div>
                <ProgressBar
                  animated
                  now={asyncProgress.pct}
                  variant='primary'
                  style={{height: '10px'}}
                />
              </div>
            )}

            {fetchingUnmatched && (
              <div className='text-center text-muted mb-2'>
                <Spinner animation='border' size='sm' className='me-1' />
                {intl.formatMessage(M.loadingUnmatched)}
              </div>
            )}

            {syncError && (
              <Alert variant='danger'>
                {intl.formatMessage(
                  {id: 'common.error.withMessage', defaultMessage: 'Error: {message}'},
                  {
                    message:
                      typeof syncError === 'string'
                        ? syncError
                        : intl.formatMessage(M.uploadFailed),
                  }
                )}
              </Alert>
            )}
          </>
        ) : (
          <>
            <Alert variant={importResult.success ? 'success' : 'danger'}>
              {importResult.message}
            </Alert>

            <div className='d-flex gap-3 mb-3'>
              <div>
                <strong>{intl.formatMessage(M.statProcessed)}</strong>{' '}
                <Badge bg='primary' text='light'>
                  {importResult.processedCount}
                </Badge>
              </div>
              <div>
                <strong>{intl.formatMessage(M.statMatched)}</strong>{' '}
                <Badge bg='success'>{importResult.matchedCount}</Badge>
              </div>
              <div>
                <strong>{intl.formatMessage(M.statUnmatched)}</strong>{' '}
                <Badge bg='warning' text='dark'>
                  {importResult.unmatchedCount}
                </Badge>
              </div>
              <div>
                <strong>{intl.formatMessage(M.statErrors)}</strong>{' '}
                <Badge bg='danger'>{importResult.errorsCount}</Badge>
              </div>
            </div>

            {importResult.unmatchedRows && importResult.unmatchedRows.length > 0 && (
              <>
                <div className='d-flex justify-content-between align-items-center mb-2'>
                  <h6 className='mb-0'>
                    {intl.formatMessage(M.unmatchedRows, {
                      count: searchQuery.trim()
                        ? intl.formatMessage(M.ofTotal, {
                            shown: totalFiltered,
                            total: importResult.unmatchedRows.length,
                          })
                        : totalFiltered,
                    })}
                  </h6>
                  <Button
                    variant='outline-primary'
                    size='sm'
                    onClick={() =>
                      exportUnmatchedCsv(importResult.unmatchedRows, statusTitleByCode)
                    }
                  >
                    {intl.formatMessage(M.exportCsv)}
                  </Button>
                </div>

                <Form.Control
                  type='text'
                  placeholder={intl.formatMessage(M.phSearch)}
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className='mb-2'
                  size='sm'
                />

                <Table size='sm' striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>{intl.formatMessage(M.thRow)}</th>
                      <th>{intl.formatMessage(M.thMemberId)}</th>
                      <th>{intl.formatMessage(M.thName)}</th>
                      <th>{intl.formatMessage(M.thCarrierStatus)}</th>
                      <th>{intl.formatMessage(M.thSystemStatus)}</th>
                      <th>{intl.formatMessage(M.thAgent)}</th>
                      <th>{intl.formatMessage(M.thReason)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedRows.map((row, idx) => (
                      <tr key={idx}>
                        <td>{row.rowNumber}</td>
                        <td>{row.memberId || dash}</td>
                        <td>{row.name || dash}</td>
                        <td>{row.carrierStatusRaw || row.carrierStatusGeneric || dash}</td>
                        <td>
                          {row.systemPolicyStatus && statusTitleByCode[row.systemPolicyStatus] ? (
                            <BadgeStatePolicy
                              status={statusTitleByCode[row.systemPolicyStatus]}
                              color={statusColorByCode[row.systemPolicyStatus] || 'secondary'}
                            />
                          ) : (
                            dash
                          )}
                        </td>
                        <td>
                          {row.systemAgentName || row.systemAgentEmail ? (
                            <>
                              {row.systemAgentName || dash}
                              {row.systemAgentEmail ? ` (${row.systemAgentEmail})` : ''}
                            </>
                          ) : (
                            dash
                          )}
                        </td>
                        <td>{row.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                {totalPages > 1 && (
                  <div className='d-flex justify-content-between align-items-center'>
                    <small className='text-muted'>
                      {intl.formatMessage(M.pageOf, {page: safePage, total: totalPages})}
                    </small>
                    <Pagination size='sm' className='mb-0'>
                      <Pagination.First
                        disabled={safePage === 1}
                        onClick={() => setCurrentPage(1)}
                      />
                      <Pagination.Prev
                        disabled={safePage === 1}
                        onClick={() => setCurrentPage(safePage - 1)}
                      />
                      {paginationItems}
                      <Pagination.Next
                        disabled={safePage === totalPages}
                        onClick={() => setCurrentPage(safePage + 1)}
                      />
                      <Pagination.Last
                        disabled={safePage === totalPages}
                        onClick={() => setCurrentPage(totalPages)}
                      />
                    </Pagination>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={handleClose} disabled={isPolling}>
          {importResult ? intl.formatMessage(M.close) : intl.formatMessage(M.cancel)}
        </Button>
        {!importResult && (
          <Button
            variant='primary'
            onClick={handleUpload}
            disabled={
              isWorking ||
              !carrierId ||
              targetCarrierIds.length === 0 ||
              policyTypeIds.length === 0 ||
              !policyDateFrom ||
              !policyDateTo ||
              !asOfDate ||
              !file
            }
          >
            {isAsyncUploading ? (
              <>
                <Spinner animation='border' size='sm' className='me-1' />{' '}
                {intl.formatMessage(M.btnUploading)}
              </>
            ) : isSyncUploading ? (
              <>
                <Spinner animation='border' size='sm' className='me-1' />{' '}
                {intl.formatMessage(M.processing)}
              </>
            ) : isPolling || fetchingUnmatched ? (
              <>
                <Spinner animation='border' size='sm' className='me-1' />{' '}
                {intl.formatMessage(M.btnWorking)}
              </>
            ) : (
              intl.formatMessage(M.btnUploadProcess)
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  )
}
